/* ============================================
   CostCenterBreakdown — Internal Cost Analysis
   Right-panel component showing cost distribution
   across centrales de costo, commission, and net profit
   ============================================ */
import type { CentralCosto } from '@/lib/types'
import type { CostoEstimadoLocal } from './CostosEstimados'
import { r2, formatCurrency } from '@/lib/utils'
import { IconAlertTriangle, IconDollar, IconBarChart, IconCheck } from '@/components/icons/Icons'

interface ComisionVendedor {
  porcentaje: number
  monto: number
}

interface CostCenterBreakdownProps {
  subtotal: number
  moneda: string
  costosEstimados: CostoEstimadoLocal[]
  centrales: CentralCosto[]
  comisionVendedor?: ComisionVendedor
}

export default function CostCenterBreakdown({
  subtotal,
  moneda,
  costosEstimados,
  centrales,
  comisionVendedor,
}: CostCenterBreakdownProps) {
  const costosDirectos = r2(costosEstimados.reduce((s, c) => s + c.monto, 0))
  const disponible = r2(subtotal - costosDirectos)

  // Only active centrales
  const centralesActivas = centrales.filter(c => c.activa)
  const totalPctCentrales = r2(centralesActivas.reduce((s, c) => s + c.porcentaje, 0))
  const sumCentrales = r2(centralesActivas.reduce((s, c) => s + r2(c.porcentaje * disponible / 100), 0))

  const comisionMonto = comisionVendedor ? r2(comisionVendedor.monto) : 0
  const utilidadNeta = r2(disponible - sumCentrales - comisionMonto)

  /** Choose bar color modifier based on percentage bracket */
  const barColor = (pct: number) => {
    if (pct <= 10) return ''
    if (pct <= 25) return 'cost-center-bar__fill--success'
    if (pct <= 40) return 'cost-center-bar__fill--warning'
    return 'cost-center-bar__fill--danger'
  }

  return (
    <div className="cost-breakdown-panel">
      <div className="cost-breakdown-panel__header">
        <IconBarChart size={16} />
        <h4 className="cost-breakdown-panel__title">Analisis Interno de Costos</h4>
      </div>

      {/* Price to Client */}
      <div className="cost-breakdown-panel__row">
        <span className="cost-breakdown-panel__label">
          <IconDollar size={14} /> Precio al Cliente
        </span>
        <strong className="cost-breakdown-panel__value">{formatCurrency(subtotal, moneda)}</strong>
      </div>

      {/* Direct Costs */}
      <div className="cost-breakdown-panel__row">
        <span className="cost-breakdown-panel__label">Costos Directos</span>
        <strong className="cost-breakdown-panel__value cost-breakdown-panel__value--danger">
          -{formatCurrency(costosDirectos, moneda)}
        </strong>
      </div>

      {/* Available */}
      <div className="cost-breakdown-panel__row cost-breakdown-panel__row--highlight">
        <span className="cost-breakdown-panel__label">Disponible</span>
        <strong className={`cost-breakdown-panel__value ${disponible >= 0 ? 'cost-breakdown-panel__value--success' : 'cost-breakdown-panel__value--danger'}`}>
          {formatCurrency(disponible, moneda)}
        </strong>
      </div>

      {/* Divider */}
      <div className="cost-breakdown-panel__divider" />

      {/* Distribution by Cost Center */}
      <div className="cost-breakdown-panel__section">
        <div className="cost-breakdown-panel__section-header">
          <span>Distribucion por Central ({totalPctCentrales.toFixed(0)}%)</span>
        </div>
        {centralesActivas.length === 0 ? (
          <p className="cost-breakdown-panel__empty">Sin centrales configuradas</p>
        ) : (
          centralesActivas.map(central => {
            const monto = r2(central.porcentaje * disponible / 100)
            return (
              <div className="cost-center-bar" key={central.id}>
                <div className="cost-center-bar__header">
                  <span className="cost-center-bar__label">{central.nombre}</span>
                  <span className="cost-center-bar__value">
                    {central.porcentaje}% = {formatCurrency(monto, moneda)}
                  </span>
                </div>
                <div className="cost-center-bar__track">
                  <div
                    className={`cost-center-bar__fill ${barColor(central.porcentaje)}`}
                    style={{ width: `${Math.min(central.porcentaje, 100)}%` }}
                  />
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Seller Commission */}
      {comisionVendedor && comisionVendedor.porcentaje > 0 && (
        <>
          <div className="cost-breakdown-panel__divider" />
          <div className="cost-breakdown-panel__row">
            <span className="cost-breakdown-panel__label">
              Comision Vendedor ({comisionVendedor.porcentaje}%)
            </span>
            <strong className="cost-breakdown-panel__value cost-breakdown-panel__value--danger">
              -{formatCurrency(comisionMonto, moneda)}
            </strong>
          </div>
        </>
      )}

      {/* Divider */}
      <div className="cost-breakdown-panel__divider" />

      {/* Net Profit */}
      <div className="cost-breakdown-panel__row cost-breakdown-panel__row--total">
        <span className="cost-breakdown-panel__label">
          {utilidadNeta >= 0 ? <IconCheck size={14} /> : <IconAlertTriangle size={14} />}
          {' '}Utilidad Neta
        </span>
        <strong className={`cost-breakdown-panel__value cost-breakdown-panel__value--${utilidadNeta >= 0 ? 'success' : 'danger'}`}>
          {formatCurrency(utilidadNeta, moneda)}
        </strong>
      </div>

      {/* Alert banner for negative profit */}
      {utilidadNeta < 0 && (
        <div className="cost-breakdown-panel__alert">
          <IconAlertTriangle size={16} />
          <span>Utilidad negativa: los costos superan el precio al cliente</span>
        </div>
      )}
    </div>
  )
}
